/**
 * DPL DEW — Role-Based Authentication Service
 * 
 * Complete refactor implementing proper role-based authentication with:
 * - Super Admins (platform-level access)
 * - Tenant Users (workspace-level access)  
 * - Unassigned Users (new onboarding users)
 * 
 * This service implements the core architectural separation between identity domains.
 */

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/utils/logger';
import type {
  AuthContext,
  AuthState,
  BootstrapResult,
  UserRole,
  Permission,
  IAuthService,
  GetAuthContextResponse,
  AdminAuthContext,
  TenantAuthContext,
  UnassignedAuthContext,
  ErrorAuthContext
} from '@/types/auth';

import {
  AUTH_ERROR_CODES,
  DEFAULT_AUTH_CONFIG,
  AuthError
} from '@/types/auth';

class AuthService implements IAuthService {
  private state: AuthState = {
    context: null,
    loading: false,
    initialized: false,
    error: null,
  };

  private config = DEFAULT_AUTH_CONFIG;
  private initPromise: Promise<BootstrapResult> | null = null;

  /**
   * Initialize the application with proper role-based bootstrap
   * This is the main entry point for all authentication
   */
  async initializeApp(): Promise<BootstrapResult> {
    // Prevent multiple simultaneous initializations
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this._performBootstrap();
    return this.initPromise;
  }

  /**
   * Core bootstrap logic with role-based branching
   */
  private async _performBootstrap(): Promise<BootstrapResult> {
    try {
      this.state.loading = true;
      this.state.error = null;

      logger.log('[Auth] Starting role-based bootstrap...');

      // Step 1: Check Supabase session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        throw new AuthError(
          'Failed to get session',
          AUTH_ERROR_CODES.SESSION_EXPIRED,
          { sessionError: sessionError.message }
        );
      }

      if (!session) {
        logger.log('[Auth] No active session - user not authenticated');
        this.state.context = null;
        this.state.initialized = true;
        
        return {
          success: false,
          context: null,
          error: 'No active session',
          redirectPath: '/login'
        };
      }

      // Step 2: Get user role type with proper precedence
      const userRole = await this._getUserRoleType();
      logger.log(`[Auth] User role determined: ${userRole}`);

      // Step 3: Branch initialization by role
      switch (userRole) {
        case 'admin':
          return await this._initializeAdminContext();
        case 'tenant':
          return await this._initializeTenantContext();
        case 'unassigned':
          return await this._initializeUnassignedContext();
        default:
          throw new AuthError(
            `Unknown role type: ${userRole}`,
            AUTH_ERROR_CODES.UNKNOWN_ROLE,
            { detectedRole: userRole }
          );
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.error('[Auth] Bootstrap failed:', errorMessage);
      
      this.state.error = errorMessage;
      this.state.initialized = true;

      return {
        success: false,
        context: null,
        error: errorMessage,
        redirectPath: '/login'
      };
    } finally {
      this.state.loading = false;
    }
  }

  /**
   * Get user role type with proper precedence
   */
  private async _getUserRoleType(): Promise<UserRole> {
    const { data, error } = await supabase.rpc('get_user_role_type');
    
    if (error) {
      throw new AuthError(
        'Failed to get user role',
        AUTH_ERROR_CODES.NETWORK_ERROR,
        { rpcError: error.message }
      );
    }
    
    return data as UserRole;
  }

  /**
   * Initialize admin context - completely bypasses tenant logic
   */
  private async _initializeAdminContext(): Promise<BootstrapResult> {
    logger.log('[Auth] Initializing admin context...');

    const { data, error } = await supabase.rpc('initialize_admin_context');
    
    if (error) {
      throw new AuthError(
        'Failed to initialize admin context',
        AUTH_ERROR_CODES.NOT_ADMIN,
        { rpcError: error.message }
      );
    }

    const adminData = data as GetAuthContextResponse;
    const context: AdminAuthContext = {
      role: 'admin',
      user_id: adminData.user_id!,
      admin_id: adminData.admin_id!,
      admin_email: adminData.admin_email!,
      full_name: adminData.full_name!,
      admin_role: adminData.admin_role as any,
      permissions: adminData.permissions || {},
      capabilities: adminData.capabilities || [],
      tenant_id: null, // Explicitly null for admins
      is_active: adminData.is_active,
      last_login_at: adminData.last_login_at,
    };

    this.state.context = context;
    this.state.initialized = true;

    logger.log('[Auth] Admin context initialized successfully');

    return {
      success: true,
      context,
      redirectPath: '/admin'
    };
  }

  /**
   * Initialize tenant context - workspace-level bootstrap
   */
  private async _initializeTenantContext(): Promise<BootstrapResult> {
    logger.log('[Auth] Initializing tenant context...');

    const { data, error } = await supabase.rpc('initialize_tenant_context');
    
    if (error) {
      throw new AuthError(
        'Failed to initialize tenant context',
        AUTH_ERROR_CODES.NOT_TENANT,
        { rpcError: error.message }
      );
    }

    const tenantData = data as GetAuthContextResponse;
    const context: TenantAuthContext = {
      role: 'tenant',
      user_id: tenantData.user_id!,
      tenant_id: tenantData.tenant_id!,
      tenant_name: tenantData.tenant_name!,
      tenant_slug: tenantData.tenant_slug!,
      tenant_plan: tenantData.tenant_plan!,
      tenant_status: tenantData.tenant_status!,
      user_role: tenantData.user_role as any,
      user_permissions: tenantData.user_permissions || [],
      profile: tenantData.profile || null,
      is_active: tenantData.is_active,
      last_login_at: tenantData.last_login_at,
    };

    this.state.context = context;
    this.state.initialized = true;

    logger.log(`[Auth] Tenant context initialized for: ${context.tenant_name}`);

    return {
      success: true,
      context,
      redirectPath: '/app'
    };
  }

  /**
   * Initialize unassigned context - onboarding flow
   */
  private async _initializeUnassignedContext(): Promise<BootstrapResult> {
    logger.log('[Auth] User needs onboarding - initializing unassigned context...');

    const context: UnassignedAuthContext = {
      role: 'unassigned',
      user_id: await this._getUserId(),
      tenant_id: null,
      needs_onboarding: true,
      is_active: true,
      last_login_at: new Date().toISOString(),
    };

    this.state.context = context;
    this.state.initialized = true;

    return {
      success: true,
      context,
      redirectPath: '/?setup=1'
    };
  }

  /**
   * Get current user ID safely
   */
  private async _getUserId(): Promise<string | null> {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.user?.id || null;
  }

  /**
   * Get current authentication context
   */
  getAuthContext(): AuthContext | null {
    return this.state.context;
  }

  /**
   * Get current authentication state
   */
  getState(): AuthState {
    return { ...this.state };
  }

  /**
   * Clear all caches and reset state
   */
  clearCache(): void {
    this.state = {
      context: null,
      loading: false,
      initialized: false,
      error: null,
    };
    this.initPromise = null;
    logger.log('[Auth] Cache cleared');
  }

  /**
   * Role checking methods
   */
  isAdmin(): boolean {
    return this.state.context?.role === 'admin';
  }

  isTenant(): boolean {
    return this.state.context?.role === 'tenant';
  }

  isUnassigned(): boolean {
    return this.state.context?.role === 'unassigned';
  }

  hasRole(role: UserRole): boolean {
    return this.state.context?.role === role;
  }

  /**
   * Permission checking methods
   */
  async hasPermission(permission: Permission): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('has_permission', { permission_name: permission });
      
      if (error) {
        logger.warn('[Auth] Permission check failed:', error.message);
        return false;
      }
      
      return Boolean(data);
    } catch (error) {
      logger.error('[Auth] Permission check error:', error);
      return false;
    }
  }

  async hasPermissions(permissions: Permission[]): Promise<boolean> {
    const results = await Promise.all(
      permissions.map(permission => this.hasPermission(permission))
    );
    return results.every(Boolean);
  }

  /**
   * Getter methods
   */
  getUserId(): string | null {
    return this.state.context?.user_id || null;
  }

  getTenantId(): string | null {
    if (this.state.context?.role === 'tenant') {
      return this.state.context.tenant_id;
    }
    return null;
  }

  getAdminId(): string | null {
    if (this.state.context?.role === 'admin') {
      return this.state.context.admin_id;
    }
    return null;
  }

  /**
   * Reset for logout
   */
  reset(): void {
    this.clearCache();
    logger.log('[Auth] Service reset for logout');
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    const { data: { session } } = await supabase.auth.getSession();
    return Boolean(session);
  }

  /**
   * Refresh authentication state
   */
  async refresh(): Promise<BootstrapResult> {
    this.clearCache();
    return this.initializeApp();
  }
}

// Export singleton instance
export const authService = new AuthService();

// Export types for convenience
export type {
  AuthContext,
  AuthState,
  BootstrapResult,
  UserRole,
  Permission,
  AdminAuthContext,
  TenantAuthContext,
  UnassignedAuthContext,
  ErrorAuthContext
};
